import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Form, Spinner, Button } from 'react-bootstrap';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
} from 'recharts';
import axios from '../api/axios';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import OutletDetailsModal from './OutletDetailsModal';
import { useNavigate } from 'react-router-dom';

const FILTER_OPTIONS = [
    { label: 'Total Sales', value: 'totalSales', dataKey: 'totalRevenue' },
    { label: 'Net Sales', value: 'netSales', dataKey: 'netSaleRevenue' },
    { label: 'Orders', value: 'orders', dataKey: 'totalOrders' },
];

const SORT_OPTIONS = [
    { label: 'Top 10 Outlets', value: '-1' },
    { label: 'Bottom 10 Outlets', value: '1' },
];

const DATE_FILTER_OPTIONS = [
    { label: 'Today', value: 'today' },
    { label: 'Yesterday', value: 'yesterday' },
    { label: 'Last 7 Days', value: 'last7days' },
    { label: 'Last 30 Days', value: 'last30days' },
    { label: 'This Month', value: 'thisMonth' },
    { label: 'Last Month', value: 'lastMonth' },
    { label: 'Custom Range', value: 'custom' },  // ← add this
];

const TABLE_COLUMNS = [
    { key: 'outletName', label: 'Outlet Name', sortable: true, isNumeric: false, type: 'text' },
    { key: 'netOrders', label: 'Total Orders', sortable: true, isNumeric: true, type: 'count' },
    { key: 'cancelledOrders', label: 'Cancelled Orders', sortable: true, isNumeric: true, type: 'count' },
    { key: 'totalRevenue', label: 'Total Sales', sortable: true, isNumeric: true, type: 'amount' },
    { key: 'netSaleRevenue', label: 'Net Sales', sortable: true, isNumeric: true, type: 'amount' },
    { key: 'totalCancelledOrderAmount', label: 'Cancelled Amount', sortable: true, isNumeric: true, type: 'amount' },
    { key: 'appNetSaleRevenue', label: 'App Net Revenue', sortable: true, isNumeric: true, type: 'amount' },
    { key: 'kioskNetSale', label: 'Kiosk Net Sale', sortable: true, isNumeric: true, type: 'amount' },
];

const SUMMARY_CARDS = [
    { key: 'totalRevenue', label: 'Total Sales', icon: 'bi-currency-rupee', color: '#4d8af0', bg: '#eef3ff', prefix: '₹' },
    { key: 'netSaleRevenue', label: 'Net Sales', icon: 'bi-receipt', color: '#20c997', bg: '#e6faf5', prefix: '₹' },
    { key: 'kioskNetSale', label: 'Kiosk Net Sales', icon: 'bi-display', color: '#fd7e14', bg: '#fff3e6', prefix: '₹' },
    { key: 'appNetSaleRevenue', label: 'App Net Sales', icon: 'bi-phone', color: '#6f42c1', bg: '#f3eeff', prefix: '₹' },
];

const formatCardValue = (val, isCount = false) => {
    if (val === undefined || val === null) return isCount ? '0' : '₹0.00';
    const num = Number(val);
    if (isCount) return num.toLocaleString('en-IN');
    return `₹${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const OutletRevenueKpi = ({ outletFilter, loadingOutlets }) => {

    const token = localStorage.getItem('authToken');
    const navigate = useNavigate();

    const [viewMode, setViewMode] = useState('list');
    const [filterType, setFilterType] = useState('totalSales'); // backend param value
    const [sortType, setSortType] = useState('-1');
    const [dateFilter, setDateFilter] = useState('today');
    const [outletData, setOutletData] = useState([]);
    const [totalsData, setTotalsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    // Get the response dataKey that maps to the current filterType
    const currentFilterOption = FILTER_OPTIONS.find((f) => f.value === filterType) || FILTER_OPTIONS[0];
    const currentDataKey = currentFilterOption.dataKey;   // e.g. 'totalRevenue', 'totalOrders'
    const selectedFilterLabel = currentFilterOption.label;
    const isAmount = currentDataKey !== 'totalOrders';


    // const [customStartDate, setCustomStartDate] = useState(null);
    // const [customEndDate, setCustomEndDate] = useState(null);
    // const [customDateError, setCustomDateError] = useState('');
    // const [appliedCustomRange, setAppliedCustomRange] = useState(null);

    const getDefaultStartDate = () => {
        const d = new Date();
        d.setDate(d.getDate() - 91);
        return d;
    };

    const [customStartDate, setCustomStartDate] = useState(getDefaultStartDate);
    const [customEndDate, setCustomEndDate] = useState(() => new Date());
    const [customDateError, setCustomDateError] = useState('');
    const [appliedCustomRange, setAppliedCustomRange] = useState(null);

    //View Modal States
    const [viewModal, setViewModal] = useState(false);
    const [openViewModal, setOpenViewModal] = useState(false);
    const [viewModalLoading, setViewModalLoading] = useState(false);
    const [viewModalData, setViewModalData] = useState(null);
    const [selectedOutletName, setSelectedOutletName] = useState('');

    const fetchOutletRevenueStats = useCallback(async () => {
        try {
            setLoading(true);
            // Send backend-expected filter values: totalSales | netSales | orders
            setLoading(true);
            let url = `dashboard/getAllOutletRevenueStats?filter=${filterType}&sort=${sortType}`;

            if (dateFilter === 'custom' && appliedCustomRange) {
                url += `&startDate=${appliedCustomRange.start}&endDate=${appliedCustomRange.end}`;
            } else {
                url += `&dateFilter=${dateFilter}`;
            }

            if (outletFilter && outletFilter !== 'all') {
                url += `&outletId=${outletFilter}`;
            }
            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response?.data?.status === true) {
                const inner = response?.data?.data?.data;
                setOutletData(inner?.outletSales || []);
                setTotalsData(inner?.totalRevenueOfAllOutlets || null);
            }
        } catch (error) {
            console.error('Error fetching outlet revenue stats:', error);
        } finally {
            setLoading(false);
        }
    }, [token, filterType, sortType, dateFilter, outletFilter, appliedCustomRange]);

    const handleApplyCustomRange = () => {
        if (!customStartDate || !customEndDate) {
            setCustomDateError('Please select both dates.');
            return;
        }
        const diffDays = Math.ceil((customEndDate - customStartDate) / (1000 * 60 * 60 * 24));
        if (diffDays > 92) {
            setCustomDateError('Range cannot exceed 92 days.');
            return;
        }
        setCustomDateError('');
        setAppliedCustomRange({
            start: customStartDate.toISOString().split('T')[0],
            end: customEndDate.toISOString().split('T')[0],
        });
    };

    const handleViewModal = (col, outlet) => {
        navigate(`/dashboard/outlet-details/${outlet?._id}`, {
            state: {
                outletName: outlet?.outletName,
                dateFilter,                    // passes 'today' | 'yesterday' | etc.
                customRange: appliedCustomRange, // passes { start, end } or null
            },
        });
    };

    const getOutletOrdersDetails = async (outletId, dateF) => {
        try {
            setViewModalLoading(true);
            const headers = { Authorization: `Bearer ${token}` };

            let url = `/dashboard/getRevenueStatThroughSummaryByOutlet?outletId=${outletId}`;

            if (dateF === 'custom' && appliedCustomRange) {
                url += `&filterType=custom&startDate=${appliedCustomRange.start}&endDate=${appliedCustomRange.end}`;
            } else {
                url += `&filterType=${dateFilter}`;
            }

            const response = await axios.get(url, { headers });
            setViewModalData(response?.data?.data);
        } catch (error) {
            console.log("Error in Getting Outlet Order Details :", error);
        } finally {
            setViewModalLoading(false);
        }
    }

    useEffect(() => {
        fetchOutletRevenueStats();
    }, [fetchOutletRevenueStats]);

    const truncateName = (name, maxLen = 14) =>
        name?.length > maxLen ? name.slice(0, maxLen) + '...' : name;

    // Enrich outlet data with calculated fields
    const processedOutletData = useMemo(() =>
        outletData.map((outlet) => ({
            ...outlet,
            netOrders: (Number(outlet.totalOrders) || 0) - (Number(outlet.cancelledOrders) || 0),
            netAppOrders: (Number(outlet.appOrders) || 0) - (Number(outlet.cancelledAppOrders) || 0),
            netKioskOrders: (Number(outlet.kioskOrders) || 0) - (Number(outlet.cancelledKioskOrders) || 0),
        })),
        [outletData]);

    // Chart data: use currentDataKey to read the correct field from response
    const chartData = useMemo(() => {
        let data = processedOutletData.map((outlet) => ({
            name: truncateName(outlet?.outletName || ''),
            fullName: outlet?.outletName || '',
            value: Number(outlet?.[currentDataKey]) || 0,
        }));

        // If filterType is 'orders', backend sorts by totalOrders which equals our value — fine.
        // But if Bottom 10 / Top 10 needs client re-sort for safety:
        if (filterType === 'orders') {
            data = [...data].sort((a, b) =>
                sortType === '-1' ? b.value - a.value : a.value - b.value
            );
        }

        return data;
    }, [processedOutletData, currentDataKey, filterType, sortType]);

    const handleTableSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    const sortedData = useMemo(() =>
        [...processedOutletData].sort((a, b) => {
            if (!sortConfig.key) return 0;
            const valA = a?.[sortConfig.key] ?? 0;
            const valB = b?.[sortConfig.key] ?? 0;
            if (typeof valA === 'string') {
                return sortConfig.direction === 'asc'
                    ? valA.localeCompare(valB)
                    : valB.localeCompare(valA);
            }
            return sortConfig.direction === 'asc' ? valA - valB : valB - valA;
        }),
        [processedOutletData, sortConfig]);

    const CustomTooltip = ({ active, payload }) => {
        if (!active || !payload?.length) return null;
        const val = payload[0]?.value;
        const isCount = !isAmount;
        return (
            <div style={{
                background: '#fff', border: '1px solid #e0e0e0',
                borderRadius: 8, padding: '8px 14px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}>
                <p className="mb-1 fw-semibold" style={{ fontSize: 13 }}>
                    {payload[0]?.payload?.fullName}
                </p>
                <p className="mb-0 text-primary" style={{ fontSize: 13 }}>
                    {selectedFilterLabel}:{' '}
                    <strong>
                        {isCount
                            ? Number(val).toLocaleString('en-IN')
                            : `₹${Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        }
                    </strong>
                </p>
            </div>
        );
    };

    // Totals calculated from all returned outlets
    const calculatedTotals = useMemo(() => {
        const acc = processedOutletData.reduce((a, outlet) => {
            TABLE_COLUMNS.forEach((col) => {
                if (col.isNumeric) {
                    a[col.key] = (a[col.key] || 0) + (Number(outlet[col.key]) || 0);
                }
            });
            return a;
        }, {});
        acc.netOrders = (acc.totalOrders || 0) - (acc.cancelledOrders || 0);
        return acc;
    }, [processedOutletData]);

    const getNetOrdersTotal = () => {
        const total = totalsData?.totalOrders ?? calculatedTotals?.totalOrders ?? 0;
        const cancelled = totalsData?.cancelledOrders ?? calculatedTotals?.cancelledOrders ?? 0;
        return total - cancelled;
    };

    const formatCell = (col, outlet) => {
        let val = outlet?.[col.key];
        if (col.key === 'netOrders') {
            val = (Number(outlet?.totalOrders) || 0) - (Number(outlet?.cancelledOrders) || 0);
        }
        if (!col.isNumeric) return val || '—';
        if (val === undefined || val === null) return col.type === 'amount' ? '₹0.00' : '0';
        if (col.type === 'amount') {
            return `₹${Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        }
        return Number.isInteger(val) ? val.toLocaleString('en-IN') : Number(val).toFixed(0);
    };

    const formatTotalCell = (col) => {
        if (!col.isNumeric) return '—';
        let val = calculatedTotals?.[col.key];
        if (col.key === 'netOrders') {
            const netOrders = (totalsData?.totalOrders ?? calculatedTotals?.totalOrders ?? 0)
                - (totalsData?.cancelledOrders ?? calculatedTotals?.cancelledOrders ?? 0);
            return netOrders.toLocaleString('en-IN');
        }
        if (val === undefined || val === null) return col.type === 'amount' ? '₹0.00' : '0';
        if (col.type === 'amount') {
            return `₹${Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        }
        return Number.isInteger(val) ? val.toLocaleString('en-IN') : Number(val).toFixed(0);
    };

    return (
        <Container fluid className="py-3 shadow-sm" style={{ backgroundColor: 'white' }}>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-1 mt-0">
                <h4 className="fw-bold mb-0">OUTLET STATISTICS</h4>

                {/* <div className="d-flex align-items-center bg-light p-1 ps-2 rounded">
                    <i className="bi bi-calendar-event me-2 text-primary" />
                    <div className="d-flex flex-column">
                        <Form.Select
                            value={dateFilter}
                            onChange={(e) => setDateFilter(e.target.value)}
                            style={{ width: '145px' }}
                            className="border-0 bg-light"
                        >
                            {DATE_FILTER_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </Form.Select>
                        {['last7days', 'last30days', 'thisMonth', 'lastMonth'].includes(dateFilter) && (
                            <span style={{ fontSize: 10, color: '#888', marginTop: 2, paddingLeft: 2 }}>
                                * Today's data not included
                            </span>
                        )}
                    </div>
                </div> */}
                <div className="d-flex align-items-center bg-light p-1 ps-2 rounded" style={{ flexWrap: 'wrap', gap: 8 }}>
                    <i className="bi bi-calendar-event me-2 text-primary" />

                    <div>
                        <Form.Select
                            value={dateFilter}
                            // onChange={(e) => {
                            //     setDateFilter(e.target.value);
                            //     setAppliedCustomRange(null);
                            //     setCustomStartDate(null);
                            //     setCustomEndDate(null);
                            //     setCustomDateError('');
                            // }}
                            onChange={(e) => {
                                const val = e.target.value;
                                setDateFilter(val);
                                setCustomDateError('');
                                if (val === 'custom') {
                                    const start = getDefaultStartDate();
                                    const end = new Date();
                                    setCustomStartDate(start);
                                    setCustomEndDate(end);
                                    setAppliedCustomRange({
                                        start: start.toISOString().split('T')[0],
                                        end: end.toISOString().split('T')[0],
                                    });
                                } else {
                                    setAppliedCustomRange(null);
                                    setCustomStartDate(null);
                                    setCustomEndDate(null);
                                }
                            }}
                            style={{ width: '145px' }}
                            className="border-0 bg-light"
                        >
                            {DATE_FILTER_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </Form.Select>
                        {['last7days', 'last30days', 'thisMonth', 'lastMonth'].includes(dateFilter) && (
                            <span style={{ fontSize: 10, color: '#888', width: '100%', paddingLeft: 2 }}>
                                * Today's data not included
                            </span>
                        )}
                    </div>

                    {dateFilter === 'custom' && (
                        <div className="d-flex align-items-center gap-2">
                            {/* Start Date */}
                            <div className="d-flex flex-column">
                                <span style={{ fontSize: 10, color: '#3f3f3f', marginBottom: 2 }}>Start date</span>
                                <DatePicker
                                    selected={customStartDate}
                                    onChange={(date) => {
                                        setCustomStartDate(date);
                                        if (customEndDate) {
                                            const diff = Math.ceil((customEndDate - date) / (1000 * 60 * 60 * 24));
                                            if (diff > 92 || diff < 0) setCustomEndDate(null);
                                        }
                                        setCustomDateError('');
                                    }}
                                    selectsStart
                                    startDate={customStartDate}
                                    endDate={customEndDate}
                                    minDate={new Date(new Date().setDate(new Date().getDate() - 92))}  // ← add this
                                    maxDate={new Date()}
                                    dateFormat="dd-MM-yyyy"
                                    placeholderText="Start date"
                                    className="form-control form-control-sm"
                                    wrapperClassName="date-picker-wrapper"
                                />
                            </div>

                            <span style={{ fontSize: 13, color: '#888', marginTop: 14 }}>→</span>

                            {/* End Date */}
                            <div className="d-flex flex-column">
                                <span style={{ fontSize: 10, color: '#3f3f3f', marginBottom: 2 }}>End date</span>
                                <DatePicker
                                    selected={customEndDate}
                                    onChange={(date) => {
                                        setCustomEndDate(date);
                                        setCustomDateError('');
                                    }}
                                    selectsEnd
                                    startDate={customStartDate}
                                    endDate={customEndDate}
                                    minDate={customStartDate || new Date(new Date().setDate(new Date().getDate() - 90))}
                                    maxDate={
                                        customStartDate
                                            ? new Date(Math.min(
                                                new Date(customStartDate).setDate(customStartDate.getDate() + 92),
                                                new Date()  // ← never exceed today
                                            ))
                                            : new Date()
                                    }
                                    disabled={!customStartDate}
                                    dateFormat="dd-MM-yyyy"
                                    placeholderText="End date"
                                    className="form-control form-control-sm"
                                    wrapperClassName="date-picker-wrapper"
                                />
                            </div>

                            {/* Invisible spacer label to align button */}
                            <div className="d-flex flex-column">
                                <span style={{ fontSize: 10, color: 'transparent', marginBottom: 2 }}>-</span>
                                <Button
                                    size="sm"
                                    variant="primary"
                                    onClick={handleApplyCustomRange}
                                    disabled={!customStartDate || !customEndDate}
                                >
                                    Apply
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Subtext messages — wrap to next line */}

                    {dateFilter === 'custom' && customDateError && (
                        <span style={{ fontSize: 11, color: '#dc3545', width: '100%', paddingLeft: 2 }}>
                            ⚠ {customDateError}
                        </span>
                    )}
                </div>
            </div>
            <hr />

            {/* Summary Cards */}
            <div className="row g-3 mb-4">
                {SUMMARY_CARDS.map((card) => {
                    const displayValue = totalsData?.[card.key] ?? calculatedTotals?.[card.key];
                    return (
                        <div key={card.key} className="col-6 col-md-3">
                            <div style={{
                                background: card.bg, borderRadius: 12, padding: '16px 18px',
                                borderLeft: `4px solid ${card.color}`, height: '100%',
                                display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                            }}>
                                <div className="d-flex justify-content-between align-items-start">
                                    <span style={{ fontSize: 11, fontWeight: 600, color: '#666', textTransform: 'uppercase', letterSpacing: '0.4px', lineHeight: 1.3 }}>
                                        {card.label}
                                    </span>
                                    <i className={`bi ${card.icon}`} style={{ fontSize: 18, color: card.color, opacity: 0.85 }} />
                                </div>

                                {loading || loadingOutlets ? (
                                    <Spinner animation="border" size="sm" style={{ color: card.color, marginTop: 8 }} />
                                ) : (
                                    <div style={{ fontSize: 18, fontWeight: 600, color: '#1a1a2e', marginTop: 2, letterSpacing: '-0.5px' }}>
                                        {card.key === 'totalRevenue' && (
                                            <div className="mt-0">
                                                <span style={{ fontSize: 12, fontWeight: 500, color: '#d75151' }}>
                                                    ₹{(totalsData?.totalCancelledOrderAmount ?? calculatedTotals?.totalCancelledOrderAmount ?? 0)
                                                        .toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    {' '}/ {totalsData?.cancelledOrders ?? calculatedTotals?.cancelledOrders ?? 0} Cancelled
                                                </span>
                                                <br />
                                            </div>
                                        )}
                                        {formatCardValue(displayValue)}
                                        {card.key === 'totalRevenue' && (
                                            <span style={{ fontSize: 12, fontWeight: 500, color: '#666', marginLeft: 6 }}>
                                                / {getNetOrdersTotal().toLocaleString('en-IN')} Orders
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* View Toggle */}
            <div className="d-flex justify-content-end mb-3">
                <div className="d-flex" style={{ border: '1px solid #dee2e6', borderRadius: 8, overflow: 'hidden' }}>
                    {['list', 'graph'].map((mode) => (
                        <Button
                            key={mode}
                            variant={viewMode === mode ? 'primary' : 'light'}
                            className="rounded-0 px-4"
                            style={{ border: 'none', fontWeight: viewMode === mode ? 600 : 400 }}
                            onClick={() => setViewMode(mode)}
                        >
                            {viewMode === mode && <i className="bi bi-check2 me-1" />}
                            {mode.charAt(0).toUpperCase() + mode.slice(1)}
                        </Button>
                    ))}
                </div>
            </div>

            {/* Graph View */}
            {viewMode === 'graph' && (
                <>
                    <div className="d-flex gap-2 mb-3 flex-wrap">
                        <Form.Select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            style={{ width: '160px' }}
                        >
                            {FILTER_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </Form.Select>

                        <Form.Select
                            value={sortType}
                            onChange={(e) => setSortType(e.target.value)}
                            style={{ width: '180px' }}
                        >
                            {SORT_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </Form.Select>
                    </div>

                    <div className="bg-light p-3 rounded shadow-sm">
                        {loading || loadingOutlets ? (
                            <div className="d-flex justify-content-center align-items-center" style={{ height: 300 }}>
                                <Spinner animation="border" variant="primary" />
                            </div>
                        ) : chartData.length === 0 ? (
                            <div className="d-flex justify-content-center align-items-center text-muted" style={{ height: 300 }}>
                                No data available
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height={500}>
                                <BarChart data={chartData} barSize={80} margin={{ top: 20, right: 30, left: 10, bottom: 20 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e0e0e0" />
                                    <XAxis
                                        dataKey="name"
                                        tick={{ fontSize: 11, fill: '#666', fontWeight: 600 }}
                                        angle={-20}
                                        textAnchor="end"
                                        interval={0}
                                    />
                                    <YAxis
                                        tick={{ fontSize: 11, fill: '#666', fontWeight: 600 }}
                                        tickFormatter={(v) =>
                                            isAmount
                                                ? v >= 1000 ? `₹${(v / 1000).toFixed(1)}K` : `₹${v}`
                                                : v.toLocaleString('en-IN')
                                        }
                                    />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend
                                        verticalAlign="bottom"
                                        wrapperStyle={{ paddingTop: 40 }}
                                        formatter={() => selectedFilterLabel}
                                    />
                                    <Bar dataKey="value" fill="#4d8af0" radius={[4, 4, 0, 0]} animationDuration={800} />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </>
            )}

            {/* List View */}
            {viewMode === 'list' && (
                <div style={{ overflowX: 'auto' }}>
                    {loading || loadingOutlets ? (
                        <div className="d-flex justify-content-center align-items-center" style={{ height: 300 }}>
                            <Spinner animation="border" variant="primary" />
                        </div>
                    ) : (
                        <table className="w-100" style={{ borderCollapse: 'separate', borderSpacing: 0, fontSize: 13 }}>
                            <thead>
                                <tr>
                                    {TABLE_COLUMNS.map((col) => (
                                        <th
                                            key={col.key}
                                            style={{
                                                padding: '14px 16px', fontSize: 12, fontWeight: 600,
                                                textTransform: 'uppercase', letterSpacing: '0.3px', color: '#6b7280',
                                                textAlign: col.type === 'amount' ? 'right' : col.type === 'count' ? 'center' : 'left',
                                                borderBottom: '1px solid #e5e7eb', backgroundColor: '#ffffff',
                                                whiteSpace: 'nowrap', cursor: col.sortable ? 'pointer' : 'default',
                                            }}
                                            onClick={() => col.sortable && handleTableSort(col.key)}
                                        >
                                            <span style={{
                                                display: 'flex', alignItems: 'center', gap: '6px',
                                                justifyContent: col.type === 'amount' ? 'flex-end' : col.type === 'count' ? 'center' : 'flex-start',
                                            }}>
                                                {col.label}
                                                {col.sortable && (
                                                    <span style={{ color: sortConfig.key === col.key ? '#2563eb' : '#d1d5db', fontSize: 11 }}>
                                                        {sortConfig.key === col.key
                                                            ? sortConfig.direction === 'asc' ? '▲' : '▼'
                                                            : '◆'}
                                                    </span>
                                                )}
                                            </span>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {/* Totals Row */}
                                <tr style={{ backgroundColor: '#f9fafb', fontWeight: 600 }}>
                                    <td style={{ padding: '12px 16px', color: '#111827' }}>Total</td>
                                    {TABLE_COLUMNS?.slice(1).map((col) => (
                                        <td
                                            key={col.key}
                                            style={{
                                                padding: '12px 16px',
                                                textAlign: col.type === 'amount' ? 'right' : col.type === 'count' ? 'center' : 'left',
                                                color: col.key === 'cancelledOrders' || col.key === 'totalCancelledOrderAmount' ? '#dc3545' : '#111827',
                                                fontWeight: col.key === 'cancelledOrders' || col.key === 'totalCancelledOrderAmount' ? 700 : 600,
                                            }}
                                        >
                                            {formatTotalCell(col)}
                                        </td>
                                    ))}
                                </tr>

                                {/* Data Rows */}
                                {sortedData.length === 0 ? (
                                    <tr>
                                        <td colSpan={TABLE_COLUMNS.length} style={{ padding: '40px 16px', textAlign: 'center', color: '#9ca3af' }}>
                                            No outlet data available
                                        </td>
                                    </tr>
                                ) : (
                                    sortedData.map((outlet, idx) => (
                                        <tr
                                            key={outlet?._id || idx}
                                            style={{ borderBottom: '1px solid #f3f4f6', borderLeft: '3px solid transparent', cursor: 'pointer', transition: 'background 0.12s, border-left-color 0.12s' }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = '#eff6ff';
                                                e.currentTarget.style.borderLeftColor = '#2563eb';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = 'transparent';
                                                e.currentTarget.style.borderLeftColor = 'transparent';
                                            }}
                                        >
                                            <td onClick={() => handleViewModal({ isNumeric: false, key: "outletName", label: "Outlet Name", sortable: true, type: "text" }, outlet)} style={{ padding: '12px 16px', color: '#2563eb', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
                                                {outlet?.outletName || '—'}
                                                <span className="row-arrow" style={{ fontSize: 13, color: '#9ca3af' }}>›</span>
                                            </td>
                                            {TABLE_COLUMNS?.slice(1).map((col) => (
                                                <td
                                                    key={col.key}
                                                    onClick={() => handleViewModal(col, outlet)}
                                                    style={{
                                                        padding: '12px 16px',
                                                        textAlign: col.type === 'amount' ? 'right' : col.type === 'count' ? 'center' : 'left',
                                                        color: col.key === 'cancelledOrders' || col.key === 'totalCancelledOrderAmount' ? '#ef4444' : '#374151',
                                                        fontWeight: col.type === 'amount' ? 500 : 400,
                                                    }}
                                                >
                                                    {formatCell(col, outlet)}
                                                </td>
                                            ))}
                                        </tr>

                                    ))
                                )}
                            </tbody>
                            <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 6 }}>
                                Click any row to view details
                            </p>
                        </table>
                    )}
                </div>
            )}

            {/* View Outlet Order Details Modal */}
            {openViewModal && (
                <OutletDetailsModal
                    isOpen={openViewModal}
                    onClose={() => { setOpenViewModal(false); setViewModalData(null); }}
                    outletName={selectedOutletName}
                    data={viewModalData}
                    loading={viewModalLoading}
                />
            )}
        </Container>
    );
};

OutletRevenueKpi.propTypes = {
    outletFilter: PropTypes.string.isRequired,
    loadingOutlets: PropTypes.bool.isRequired,
};

export default OutletRevenueKpi;